import Navbar from "./Navbar";
import Footer from "./Footer";
import { useEffect, useState } from "react";
import axios from "../../api";

import { ChevronDown, Menu, Star } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { FaHeart, FaRegHeart } from "react-icons/fa";



export default function AllProducts() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [showSidebar, setShowSidebar] = useState(false);

  const [toast, setToast] = useState({ show: false, message: "", type: "" });

  const { search } = useLocation();
  const query = new URLSearchParams(search).get("q");


  // Load categories
  useEffect(() => {
   axios.get("/api/booking-categories/").then((res) => setCategories(res.data));
  }, []);

  // Load all products
  useEffect(() => {
    axios.get("/api/products/").then((res) => setProducts(res.data));
  }, []);


  useEffect(() => {
  if (query && products.length > 0) {
    const filtered = products.filter((p) =>
      p.title.toLowerCase().includes(query.toLowerCase())
    );
    setProducts(filtered);
  }
}, [query]);


  // Load products by category
  const loadCategory = (slug) => {
    setSelectedCategory(slug);

    if (slug === "all") {
      axios.get("/api/products/").then((res) => setProducts(res.data));
    } else {
      axios.get(`/api/products/booking-category/${slug}/`).then((res) => setProducts(res.data));
    }

    setShowSidebar(false);
  };


  const showToast = (message, type = "success") => {
  setToast({ show: true, message, type });

  setTimeout(() => {
    setToast({ show: false, message: "", type: "" });
  }, 2000);
};



  const toggleWishlist = async (productId) => {
  try {
    const res = await axios.post(
      "/api/wishlist/toggle/",
      { product_id: productId },
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        },
      }
    );

    const updatedProduct = res.data.product;
    const wishlisted = res.data.wishlisted;

    // update UI
    setProducts(prev =>
      prev.map(item =>
        item.id === updatedProduct.id ? updatedProduct : item
      )
    );

    // Show toast notification
    if (wishlisted) {
      showToast("Added to your Wishlist");
    } else {
      showToast("Removed from your Wishlist");
    }

  } catch (error) {
    console.log("Wishlist error:", error);
  }
};


  return (
    <div className="bg-gray-100 min-h-screen">
      <Navbar />

      {/* Mobile Toggle */}
      <div className="md:hidden flex justify-between items-center px-4 py-3 bg-white shadow-sm sticky top-0 z-20">
        <h1 className="text-lg font-semibold text-gray-700">
          {selectedCategory === "all"
            ? "All Products"
            : `${(selectedCategory || "").toUpperCase()} Products`}
        </h1>

        <button
          onClick={() => setShowSidebar(!showSidebar)}
          className="flex items-center gap-1 text-indigo-600"
        >
          Categories {showSidebar ? <ChevronDown size={18} /> : <Menu size={18} />}
        </button>
      </div>

      <div className="w-full grid grid-cols-1 md:grid-cols-[250px_1fr] gap-4 mt-4">


        {/* Sidebar */}
        <aside
          className={`bg-white w-full p-4 rounded-lg shadow-md h-fit md:sticky md:top-24 z-10 mt-11
          ${showSidebar ? "block" : "hidden md:block"}`}
        >
          <h2 className="text-lg font-semibold mb-3 border-b pb-2">Categories</h2>

          <ul className="space-y-1">
            <li>
              <button
                onClick={() => loadCategory("all")}
                className={`w-full px-3 py-2 rounded-md font-medium text-sm ${
                  selectedCategory === "all"
                    ? "bg-indigo-600 text-white"
                    : "hover:bg-indigo-100"
                }`}
              >
                All
              </button>
            </li>

            {categories.map((cat) => (
              <li key={cat.id}>
                <button
                  onClick={() => loadCategory(cat.slug)}
                  className={`w-full px-3 py-2 rounded-md font-medium text-sm ${
                    selectedCategory === cat.slug
                      ? "bg-indigo-600 text-white"
                      : "hover:bg-indigo-100"
                  }`}
                >
                  {cat.name}
                </button>
              </li>
            ))}
          </ul>
        </aside>

        {/* Product Grid */}
        <main>
          <h2 className="text-xl font-semibold mb-4 text-center">
            {selectedCategory === "all"
              ? "All Products"
              : `${(selectedCategory || "").toUpperCase()} Products`}
          </h2>

          {products.length === 0 ? (
            <p className="text-lg text-gray-500">No products found.</p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-6">

            {products.map((p) => (
  <div key={p.id} className="bg-white rounded-2xl shadow-md hover:shadow-xl border transition transform hover:-translate-y-1">

    <div className="relative">

      {/* ❤️ Wishlist Button */}
      <button
        onClick={() => toggleWishlist(p.id)}
        className="absolute top-3 right-3 text-red-600 text-xl z-20"
      >
        {p.is_wishlisted === true ? <FaHeart /> : <FaRegHeart />}

      </button>

      <Link to={`/product/${p.id}`}>
        <div className="p-3">
          <img
            src={p.image}
            className="w-full h-48 object-contain rounded-md bg-gray-50 p-3"
          />
        </div>
      </Link>

    </div>

    <div className="px-3 pb-4">
      {/* Title */}
      <Link
        to={`/product/${p.id}`}
        className="text-sm font-medium text-gray-800 hover:text-indigo-600"
      >
        {p.title}
      </Link>

      {/* Ratings */}
      <div className="flex items-center gap-1 mt-1">
        <span className="bg-green-600 text-white px-1.5 py-0.5 rounded text-xs flex items-center gap-1">
          {p.rating || 4.2} <Star size={12} />
        </span>
        <span className="text-xs text-gray-500">
          ({p.rating_count || 1000})
        </span>
      </div>

      {/* Price */}
      <div className="mt-2 flex items-center gap-2">
        <span className="text-xl font-bold text-gray-900">
          ₹{p.discount_price || p.price}
        </span>

        {p.discount_price && (
          <>
            <span className="text-sm text-gray-500 line-through">
              ₹{p.price}
            </span>
            <span className="text-sm font-semibold text-green-600">
              {Math.round(((p.price - p.discount_price) / p.price) * 100)}% off
            </span>
          </>
        )}
      </div>
    </div>

  </div>
))}


            </div>
          )}
        </main>
      </div>

      {toast.show && (
  <div className="fixed bottom-6 left-6 z-50 bg-black text-white px-5 py-3 rounded-lg shadow-lg flex items-center gap-3 animate-slideup">

    <span className="text-green-400 text-lg">✔</span>

    <span className="font-medium">{toast.message}</span>
  </div>
)}


      <Footer />
    </div>
  );
}
